import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { findNextChallengeExercise } from '../features/leaderboard/useChallengeExercises';
import type { ChallengeExercises } from '../features/leaderboard/leaderboardApi';
import { useNextExercise } from '../features/exercises/useNextExercise';
import './ExerciseOutcomeActions.css';

export type OutcomeKind = 'passed' | 'retryable' | 'exhausted';

interface ExerciseOutcomeActionsProps {
  kind: OutcomeKind;
  exerciseId: string;
  courseId?: string;
  challenge: { courseId: string; exercises: ChallengeExercises } | null;
}

interface NextTarget {
  id: string;
  title: string;
  href: string;
}

function useNextTarget(props: ExerciseOutcomeActionsProps): { target: NextTarget | null; courseComplete: boolean } {
  const { exerciseId, courseId, challenge } = props;
  const courseNext = useNextExercise(courseId, exerciseId, !challenge);

  if (challenge) {
    const next = findNextChallengeExercise(challenge.exercises.exercises, exerciseId);
    return {
      target: next ? { ...next, href: `/exercise/${next.id}?challenge=${challenge.courseId}` } : null,
      courseComplete: false,
    };
  }
  const next = courseNext.nextExercise;
  return {
    target: next ? { id: next.id, title: next.title, href: `/exercise/${next.id}` } : null,
    courseComplete: courseNext.isCourseComplete,
  };
}

function labelKey(kind: OutcomeKind) {
  if (kind === 'passed') {
    return 'exercise.next';
  }
  return kind === 'retryable' ? 'exercise.skip' : 'exercise.continue';
}

/**
 * What the student can do next once an answer is graded: a prominent "next exercise" button after
 * a correct answer or once attempts run out, and a discreet "skip" while a retry is still possible.
 */
function ExerciseOutcomeActions(props: ExerciseOutcomeActionsProps) {
  const { t } = useTranslation();
  const { kind, challenge } = props;
  const { target, courseComplete } = useNextTarget(props);

  if (target) {
    const label =
      kind === 'passed' && challenge
        ? t('leaderboard.nextChallengeExercise', { title: target.title })
        : t(labelKey(kind), { title: target.title });
    const emphasis = kind === 'retryable' ? 'secondary' : 'primary';
    return (
      <Link className={`exercise-outcome__action exercise-outcome__action--${emphasis}`} to={target.href}>
        {label} &rarr;
      </Link>
    );
  }

  if (kind === 'passed') {
    if (challenge) {
      return <p className="exercise-outcome__message">{t('leaderboard.challengeCompleteShort')}</p>;
    }
    return courseComplete ? <p className="exercise-outcome__message">{t('exercise.courseComplete')}</p> : null;
  }
  return kind === 'exhausted' ? <p className="exercise-outcome__message">{t('exercise.noOtherExercise')}</p> : null;
}

export default ExerciseOutcomeActions;
